import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import { eur, imgW } from "@/lib/api";

export function CartDrawer() {
  const { t, lang, cart, cartOpen, setCartOpen, updateQty, removeItem, subtotal, count } = useStore();
  const nav = useNavigate();
  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="flex w-full flex-col border-l border-concrete bg-sun p-0 sm:max-w-[460px] [&>button]:hidden" data-testid="cart-drawer">
        <div className="flex items-center justify-between border-b border-concrete px-6 py-5">
          <SheetTitle className="font-display text-xl font-black uppercase tracking-tight">{t("yourBag")} <span className="font-mono text-sm font-normal text-ink/40">({count})</span></SheetTitle>
          <button onClick={() => setCartOpen(false)} aria-label="Close bag" data-testid="cart-close-btn"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6">
          {cart.length === 0 && (
            <div className="flex h-full flex-col items-start justify-center gap-6" data-testid="cart-empty">
              <p className="font-display text-3xl font-black uppercase">{t("emptyBag")}</p>
              <button className="btn-ink" onClick={() => { setCartOpen(false); nav("/shop"); }} data-testid="cart-empty-shop-btn">{t("shopNow")}</button>
            </div>
          )}
          {cart.map((it, i) => (
            <div key={`${it.product_id}-${it.size}`} className="flex gap-4 border-b border-concrete py-5" data-testid={`cart-item-${i}`}>
              <Link to={`/product/${it.slug}`} onClick={() => setCartOpen(false)} className="h-24 w-24 shrink-0 overflow-hidden bg-white">
                <img src={imgW(it.image, 300)} alt={it.name} className="h-full w-full object-contain" />
              </Link>
              <div className="flex flex-1 flex-col">
                <p className="eyebrow text-ink/50">{it.brand}</p>
                <p className="mt-1 text-sm font-medium leading-snug">{it.name}</p>
                <p className="mt-1 font-mono text-xs text-ink/60">EU {it.size}</p>
                <div className="mt-auto flex items-center justify-between pt-3">
                  <div className="flex items-center border border-concrete">
                    <button className="p-2 disabled:opacity-30" disabled={it.qty <= 1} onClick={() => updateQty(it.product_id, it.size, it.qty - 1)} aria-label="Decrease" data-testid={`cart-item-dec-${i}`}><Minus className="h-3 w-3" /></button>
                    <span className="w-8 text-center font-mono text-sm" data-testid={`cart-item-qty-${i}`}>{it.qty}</span>
                    <button className="p-2 disabled:opacity-30" disabled={it.qty >= (it.stock || 10)} onClick={() => updateQty(it.product_id, it.size, it.qty + 1)} aria-label="Increase" data-testid={`cart-item-inc-${i}`}><Plus className="h-3 w-3" /></button>
                  </div>
                  <p className="font-mono text-sm">{eur(it.price * it.qty, lang)}</p>
                </div>
                <button onClick={() => removeItem(it.product_id, it.size)} className="mt-2 w-fit font-mono text-[10px] uppercase tracking-widest text-ink/50 hover:text-ink" data-testid={`cart-item-remove-${i}`}>{t("remove")}</button>
              </div>
            </div>
          ))}
        </div>
        {cart.length > 0 && (
          <div className="border-t border-concrete px-6 py-6">
            <div className="flex items-baseline justify-between">
              <span className="eyebrow">{t("subtotal")}</span>
              <span className="font-display text-2xl font-black" data-testid="cart-subtotal">{eur(subtotal, lang)}</span>
            </div>
            <p className="mt-2 text-xs text-ink/50">{t("shippingAtCheckout")}</p>
            <button className="btn-ink mt-5 w-full" onClick={() => { setCartOpen(false); nav("/checkout"); }} data-testid="cart-checkout-btn">{t("checkout")}</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
